// @flow

import React from "react";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { connect } from "react-redux";
import {
  Button,
  Col,
  Row,
  Icons,
  DatePicker,
  Input,
  ComboBox,
  Label,
  Icon,
  View,
} from "@components";
import actionCreators from "@modules/profile/actionCreator";
import { myCurrentAbsence } from "@modules/profile/selectors";

type ComponentState = $Exact<{
  finished: boolean,
  currentReferralIdx: number,
}>;

type OwnProps = $Exact<{
  clientId: number,
}>;

type PropsFromState = $Exact<{
  absence: ?Object,
}>;

type PropsFromDispatch = $Exact<{
  submitReferral: typeof actionCreators.AddMyReferralQuestions,
}>;

type Props = $Exact<{
  ...PropsFromLocalize,
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
}>;

class ReferralQuestions extends React.Component<Props, ComponentState> {
  state = {
    finished: false,
    currentReferralIdx: 0,
    referrals: [],
  };

  componentDidMount() {
    this.setState({
      referrals: this.defaultData,
    });

    if (this.props.referrals?.length) {
      this.props.onChange({ isValid: false });
    }
  }

  validate = (): boolean => {
    const currentRef = this.getCurrentReferral();
    if (currentRef.agreedToReferral === false) {
      return currentRef.declineReason !== null;
    } else if (currentRef.agreedToReferral === true) {
      const mandatoryQuestions = currentRef.questions.filter(qa => {
        return qa.mandatory;
      });
      const noValues = mandatoryQuestions.filter(qa => {
        return (
          qa.answer === null ||
          !qa.answer?.value ||
          (Array.isArray(qa.answer?.value) && !qa.answer?.value.length)
        );
      });
      return noValues.length === 0;
    }
    return false;
  };

  get defaultData() {
    return (
      this.props.referrals.map(el => ({
        ...el,
        agreedToReferral: null,
      })) || []
    );
  }

  get referrals() {
    return this.state.referrals || this.defaultData;
  }

  acceptReferral = () => {
    let state = { ...this.state };
    state.referrals[this.state.currentReferralIdx].agreedToReferral = true;
    this.setState({ ...this.state });
  };

  declineReferral = () => {
    let state = { ...this.state };
    state.referrals[this.state.currentReferralIdx].agreedToReferral = false;
    this.setState({ ...this.state });
  };

  editAcceptDeclineReferral = () => {
    let state = { ...this.state };
    state.referrals[this.state.currentReferralIdx].agreedToReferral = null;
    state.referrals[this.state.currentReferralIdx].declineReason = null;
    state.referrals[this.state.currentReferralIdx].questions = state.referrals[
      this.state.currentReferralIdx
    ].questions.map(qa => {
      return {
        ...qa,
        answer: null,
      };
    });
    this.setState({ ...state });
  };

  submitReferral = () => {
    const currentReferral = this.getCurrentReferral();
    const questionAnswers = currentReferral.agreedToReferral
      ? currentReferral.questions.map(q => {
          if (q.type === "CONFIRMATION") {
            return {
              id: q.id,
              answer: {
                type: "CONFIRMATION",
                value: true,
              },
            };
          }

          return {
            id: q.id,
            answer: q.answer
              ? q.answer
              : {
                  type: q.type,
                  value: null,
                },
          };
        })
      : null;

    const referralToSubmit = {
      agreedToReferral: currentReferral.agreedToReferral,
      declineReason: !currentReferral.agreedToReferral ? currentReferral.declineReason : null,
      questionAnswers: questionAnswers,
    };

    this.props.submitReferral({
      clientId: this.props.clientId,
      interactionId: this.props.absence?.interactionId,
      referralId: currentReferral.id,
      data: referralToSubmit,
    });
    if (!this.goToNextReferral()) {
      this.props.onChange({ isValid: true });
      this.setState({ ...this.state, finished: true });
    }
  };

  getCurrentReferral = () => {
    return this.state.referrals[this.state.currentReferralIdx];
  };

  goToNextReferral = () => {
    let { currentReferralIdx } = this.state;
    if (this.state.referrals[++currentReferralIdx]) {
      this.setState({
        ...this.state,
        currentReferralIdx,
      });
      return true;
    }
    return false;
  };

  convertSelectData = referrals => {
    return (
      referrals &&
      referrals.map(d => {
        return { value: d, label: d };
      })
    );
  };

  generateQuestionField = (fieldType, questionIndex) => {
    const currentQuestion = this.getCurrentReferral().questions[questionIndex];
    switch (fieldType) {
      case "CONFIRMATION":
        return <div />;
      case "FREE_TEXT":
        return <Input onChange={this.updateFreeText(questionIndex, currentQuestion.type)} />;
      case "DATE_PICK":
        return <DatePicker onChange={this.updateAnswer(questionIndex, currentQuestion.type)} />;
      case "MULTI_SELECT":
        const options = this.convertSelectData(currentQuestion.selectableValues || []);
        const value =
          currentQuestion.answer && currentQuestion.answer.value
            ? options.filter(opt => currentQuestion.answer.value.includes(opt.value))
            : currentQuestion.answer?.value;

        return (
          <ComboBox
            onChange={this.updateAnswer(questionIndex, currentQuestion.type)}
            options={options}
            value={value}
            isMulti
          />
        );
      case "SINGLE_SELECT":
        return (
          <ComboBox
            onChange={this.updateSingleSelect(questionIndex, currentQuestion.type)}
            options={this.convertSelectData(currentQuestion.selectableValues)}
            value={currentQuestion.answer?.value}
          />
        );
    }
    return true;
  };

  generateQuestions = () => {
    const currentReferral = this.getCurrentReferral();
    return (
      <div className={"mb-4"}>
        {currentReferral.questions.map((qa: Object, questionIdx: number) => {
          return (
            <div className={"mb-4"} key={`$question-${questionIdx}`}>
              <span>
                {qa.text}
                {qa.mandatory && <Icon icon="asterisk" size="xs" className="text-warning ml-2" />}
              </span>
              {this.generateQuestionField(qa.type, questionIdx)}
            </div>
          );
        })}

        <View>
          <Button disabled={!this.validate()} onClick={this.submitReferral}>
            {this.props.t("SUBMIT")}
          </Button>
        </View>
      </div>
    );
  };

  generateDeclinedReasons = () => {
    const currentReferral = this.getCurrentReferral();
    return (
      <div>
        <Label for="reason">{this.props.t("REASON_FOR_DECLINE")}</Label>
        <ComboBox
          inputId="reason"
          className={"mb-4"}
          onChange={this.updateDeclinedReason}
          options={currentReferral.declineReasons.map(dr => {
            return { label: dr.reason, value: dr.id };
          })}
          value={currentReferral.declineReason?.id}
        />
        <View>
          <Button disabled={!this.validate()} onClick={this.submitReferral}>
            {this.props.t("SUBMIT")}
          </Button>
        </View>
      </div>
    );
  };

  generatePreviousReferrals = () => {
    const { referrals, currentReferralIdx } = this.state;

    return referrals.map((referral, idx: number) => {
      if (idx < currentReferralIdx || this.state.finished === true)
        return referral.agreedToReferral === true ? (
          <div key={idx} className={"mb-4"}>
            <div className="answer-banner positive d-flex">
              {referral.supplierName} {this.props.t("ACCEPTED")}
            </div>
          </div>
        ) : (
          <div key={idx} className={"mb-4"}>
            <div className="answer-banner negative d-flex">
              {referral.supplierName} {this.props.t("DECLINED")}
            </div>
          </div>
        );
      return <div key={idx} />;
    });
  };

  updateDeclinedReason = reason => {
    let state = { ...this.state };
    state.referrals[
      this.state.currentReferralIdx
    ].declineReason = this.getCurrentReferral().declineReasons.filter(
      declinedReason => reason === declinedReason.id,
    )[0];

    this.setState({ ...state });
  };

  updateFreeText = (idx: number, type: string) => ev => {
    this.updateAnswer(idx, type)(ev.currentTarget.value);
  };

  updateSingleSelect = (idx: number, type: string) => (value: string) => {
    this.updateAnswer(idx, type)(value);
  };

  updateAnswer = (idx: number, type?: string) => (val: string | Array<any>) => {
    let state = { ...this.state };
    state.referrals[this.state.currentReferralIdx].questions[idx].answer = {
      type: type || "TEXT",
      value: val,
    };
    this.setState({ ...state });
  };

  render() {
    const currentReferral = this.getCurrentReferral();
    if (!currentReferral) return <div />;
    return (
      <div
        className={"mb-4 px-5 w-100 d-flex justify-content-center flex-column referral-questions"}
      >
        <div className={"previous-referrals"}>{this.generatePreviousReferrals()}</div>
        {!this.state.finished && (
          <React.Fragment>
            <h3 className="font-weight-bolder">
              {this.props.t("REFERRAL_QUESTIONS")}: {currentReferral.supplierName} Test
            </h3>
            <p
              dangerouslySetInnerHTML={{
                __html: this.props.t("REFERRAL_QUESTIONS_LONG_TEXT", {
                  SUPPLIER_NAME: currentReferral.supplierName,
                  SUPPLIER_DESCRIPTION: currentReferral.supplierDescription,
                }),
              }}
            />
            <React.Fragment>
              {currentReferral.agreedToReferral === null && (
                <Row className="mb-4 w-100 d-flex justify-content-center ">
                  <Col sm={4} className="pl-0">
                    <Button className="btn-block" color="negative" onClick={this.declineReferral}>
                      {this.props.t("DECLINE_REFERRAL")}
                    </Button>
                  </Col>

                  <Col sm={4} className="pr-0">
                    <Button className="btn-block" color="positive" onClick={this.acceptReferral}>
                      {this.props.t("ACCEPT_REFERRAL")}
                    </Button>
                  </Col>
                </Row>
              )}
            </React.Fragment>

            <div className="d-flex flex-column">
              {currentReferral.agreedToReferral === true && (
                <React.Fragment>
                  <div className="mb-4 answer-banner positive d-flex">
                    {this.props.t("REFERRAL_ACCEPTED")}
                    <span
                      className="material-icons pointer"
                      onClick={this.editAcceptDeclineReferral}
                    >
                      edit
                    </span>
                  </div>
                  <React.Fragment>{this.generateQuestions()}</React.Fragment>
                </React.Fragment>
              )}
              {currentReferral.agreedToReferral === false && (
                <React.Fragment>
                  <div className="mb-4 answer-banner negative d-flex">
                    {this.props.t("REFERRAL_DECLINED")}{" "}
                    <span
                      className="material-icons pointer"
                      onClick={this.editAcceptDeclineReferral}
                    >
                      edit
                    </span>
                  </div>
                  <React.Fragment>{this.generateDeclinedReasons()}</React.Fragment>
                </React.Fragment>
              )}
            </div>
          </React.Fragment>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state): PropsFromState => {
  return {
    absence: myCurrentAbsence(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  submitReferral: actionCreators.AddMyReferralQuestions,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(ReferralQuestions);
